/**
 * @file index.tsx
 *
 * @fileoverview Shows a job type selection for the widget.
 */

import { FormattedMessage } from 'react-intl';
import { JobCart } from '~/jobiqo/components/Job/Wizard/components/JobCart';
import JobWizardSteps from '~/jobiqo/components/Job/Wizard/components/JobWizardSteps';
import { WizardLayout } from '~/jobiqo/components/Wizard/Layout';
import JobTypeEnum from '~/jobiqo/enums/jobType';
import { ButtonDiv } from '~/vcl/atoms/01-forms/01-button/03-button-div';
import { Grid, GridColumnsProp } from '~/vcl/atoms/06-layout/04-grid';
import { Cell } from '~/vcl/atoms/06-layout/04-grid/01-cell';
import { Spacing } from '~/vcl/atoms/06-layout/07-spacing';
import { Box } from '~/vcl/atoms/07-box';
import { Text } from '~/vcl/atoms/13-text/03-text';
import { BoxAction } from '~/vcl/organisms/02-actions/01-box-action';
import { OneSidebarLayout } from '~/vcl/templates/01-layout/02-grid-layouts/02-one-sidebar';
import styles from './wizardAddJobType.module.scss';
import { getJobTypeIcon } from '~/jobiqo/components/Slots/JobSelectionIconSlot';
import { JobWizardCurrentOrg } from '~/jobiqo/components/Job/Wizard/components/JobWizardCurrentOrg';
import JobWizardSidebarSlot from '~/jobiqo/components/Slots/JobWizardSidebarSlot';
import { useFrontendConfig } from '~/jobiqo/context/frontendConfig';

const JobTypeFieldsPerType = {
  [JobTypeEnum.JOB_PER_TEMPLATE]: {
    icon: getJobTypeIcon(JobTypeEnum.JOB_PER_TEMPLATE),
    title: (
      <FormattedMessage
        id="job_per_template"
        defaultMessage="Job per template"
      />
    ),
    desc: (
      <FormattedMessage
        id="job_per_template_desc"
        defaultMessage="Post a new job using your own text. This type of job has better user experience and performance."
      />
    ),
    chooseId: 'choose_job_per_template',
  },
  [JobTypeEnum.JOB_PER_LINK]: {
    icon: getJobTypeIcon(JobTypeEnum.JOB_PER_LINK),
    title: <FormattedMessage id="job_per_link" defaultMessage="Job per link" />,
    desc: (
      <FormattedMessage
        id="job_per_link_desc"
        defaultMessage="Post a new job using a link. We will process the content of the provided link using a content parsing tool."
      />
    ),
    chooseId: 'choose_job_per_link',
  },
  [JobTypeEnum.JOB_PER_FILE]: {
    icon: getJobTypeIcon(JobTypeEnum.JOB_PER_FILE),
    title: <FormattedMessage id="job_per_file" defaultMessage="Job per file" />,
    desc: (
      <FormattedMessage
        id="job_per_file_desc"
        defaultMessage="Post a new job using a file. We will process the content of the provided link using a content parsing tool."
      />
    ),
    chooseId: 'choose_job_per_file',
  },
};

interface JobWizardStepTypeViewProps {
  /**
   * On choose job type callback.
   */
  onChoose: (jobType: string) => void;
  /**
   * The allowed job types.
   */
  allowedJobTypes: string[];
  /**
   * The available job types.
   */
  availableJobTypes: string[];
}

export default function JobWizardStepTypeView({
  onChoose,
  allowedJobTypes,
  availableJobTypes,
}: JobWizardStepTypeViewProps) {
  const { JOBS } = useFrontendConfig();
  const HIDE_JOB_CART = JOBS.HIDE_JOB_CART;

  return (
    <WizardLayout size="xLarge" Steps={<JobWizardSteps />}>
      <OneSidebarLayout
        sidebarPosition="right"
        sidebar={
          <>
            {!HIDE_JOB_CART ? <JobCart /> : null}
            <JobWizardCurrentOrg />
            <JobWizardSidebarSlot />
          </>
        }
      >
        <Grid
          columns={[1, 1, availableJobTypes.length as GridColumnsProp]}
          alignItems="start"
        >
          {availableJobTypes.map((availableJobType, index) => {
            const disabled = !allowedJobTypes.includes(availableJobType);

            return (
              <Cell
                key={index}
                alignItems="start"
                display="grid"
                className={styles.fullHeightCell}
              >
                <Box className={styles.wrappedBox} paddingClean>
                  <BoxAction
                    asButton={true}
                    onClick={() => onChoose(availableJobType)}
                    icon={JobTypeFieldsPerType[availableJobType]['icon']}
                    disabled={disabled}
                  >
                    <h3>{JobTypeFieldsPerType[availableJobType]['title']}</h3>
                    <Text color="gray5">
                      {JobTypeFieldsPerType[availableJobType]['desc']}
                    </Text>
                    <Spacing />
                    <ButtonDiv
                      id={JobTypeFieldsPerType[availableJobType]['chooseId']}
                      buttonStyle="primary"
                      disabled={disabled}
                    >
                      <FormattedMessage
                        id="choose_job_type"
                        defaultMessage="Choose"
                      />
                    </ButtonDiv>
                  </BoxAction>
                </Box>
              </Cell>
            );
          })}
        </Grid>
        <Spacing size="large" />
      </OneSidebarLayout>
    </WizardLayout>
  );
}
export { JobWizardStepTypeView };
